import {TextEditor} from 'common/components/Fields/TextEditor'
import {calculateErrorMessage} from 'common/helpers'
import {type FC, useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useParams} from 'react-router'
import {toast} from 'react-toastify'
import {
  getExternalRecommendationRequest,
  submitExternalRecommendation,
} from 'setup/redux/Actions/CollegeCounsellingPortfolio/collegeCounsellingPortfolio'

import {KTCard, KTCardBody} from '_metronic/helpers'

import {
  bodyPlaceholderError,
  buildRecommendationPrefill,
  firstMeaningful,
  hasBodyPlaceholder,
  hasEditorContent,
  isUntouchedPrefill,
} from '../util/recommendationPrefill'

type RequestData = {
  student_name: string
  teacher_name: string
  subject: string
  status: string
  submitted_at: string | null
  recommendation: string | null
}

/**
 * Public page an external recommender lands on from their emailed link.
 *
 * There is no session here — the token in the URL is the only credential — so
 * this deliberately shows nothing beyond the request itself: who the student
 * is, what subject was asked for, and the box to write in.
 */
const ExternalRecommendation: FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const {token} = useParams()

  const [loading, setLoading] = useState(true)
  const [request, setRequest] = useState<RequestData | null>(null)
  const [loadError, setLoadError] = useState('')
  const [recommendation, setRecommendation] = useState('')
  const [recommendationError, setRecommendationError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // What we seeded the editor with, so submit can tell an untouched draft from
  // a real letter.
  const seededRef = useRef<string | null>(null)

  const loadRequest = useCallback(() => {
    if (!token) return
    setLoading(true)
    dispatch(
      getExternalRecommendationRequest(
        token,
        (res: any) => {
          const data: RequestData | null = res.result ?? null
          setRequest(data)

          // Seed the same starting draft an internal teacher gets. Only for an
          // unanswered request — a submitted one is shown read-only below.
          if (data && data.status !== 'submitted') {
            const seed = buildRecommendationPrefill({
              intl,
              studentName: data.student_name,
              authorName: firstMeaningful(data.teacher_name, ''),
              authorTitle: intl
                .formatMessage(
                  {id: 'COUNSELLING.REVIEW.SUBJECT_TEACHER'},
                  {subject: firstMeaningful(data.subject, '')}
                )
                .trim(),
            })
            setRecommendation(seed)
            seededRef.current = seed
          } else {
            seededRef.current = null
          }
          setLoading(false)
        },
        (err: any) => {
          setLoadError(calculateErrorMessage(err))
          setLoading(false)
        }
      )
    )
  }, [dispatch, token, intl])

  useEffect(() => {
    loadRequest()
  }, [loadRequest])

  const submit = () => {
    if (!hasEditorContent(recommendation)) {
      setRecommendationError(intl.formatMessage({id: 'COUNSELLING.EXTERNAL.REQUIRED'}))
      return
    }
    // The seed is content, so the check above passes on a letter nobody wrote.
    // Caught separately: the body may be written while the placeholder line
    // survives above or below it, and that would print in the final letter.
    if (isUntouchedPrefill(recommendation, seededRef.current)) {
      setRecommendationError(intl.formatMessage({id: 'COUNSELLING.EXTERNAL.REQUIRED'}))
      return
    }
    if (hasBodyPlaceholder(recommendation, intl)) {
      setRecommendationError(bodyPlaceholderError(intl))
      return
    }
    setRecommendationError(null)
    setSubmitting(true)
    dispatch(
      submitExternalRecommendation(
        token,
        {recommendation},
        () => {
          setSubmitting(false)
          toast.success(intl.formatMessage({id: 'COUNSELLING.EXTERNAL.SUBMITTED_TOAST'}))
          // Re-fetch rather than patching state: the server decides whether the
          // request now counts as answered.
          loadRequest()
        },
        (err: any) => {
          setSubmitting(false)
          toast.error(calculateErrorMessage(err))
        }
      )
    )
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center py-20'>
        <span className='spinner-border' role='status' />
      </div>
    )
  }

  // An invalid, superseded or rejected token lands here. The message stays
  // vague on purpose — this page is reachable by anyone with the URL.
  if (loadError || !request) {
    return (
      <div className='container py-10'>
        <div className='alert alert-danger d-flex align-items-center'>
          <i className='bi bi-exclamation-triangle-fill fs-2 me-3 text-danger' />
          <div>{loadError || intl.formatMessage({id: 'COUNSELLING.EXTERNAL.INVALID'})}</div>
        </div>
      </div>
    )
  }

  const alreadySubmitted = request.status === 'submitted'

  return (
    <div className='container py-8' style={{maxWidth: 900}}>
      <KTCard>
        <div className='card-header'>
          <div className='card-title'>
            <h3 className='m-0'>{intl.formatMessage({id: 'COUNSELLING.EXTERNAL.HEADING'})}</h3>
          </div>
        </div>
        <KTCardBody>
          <p className='text-gray-700 fs-5'>
            {intl.formatMessage(
              {id: 'COUNSELLING.EXTERNAL.INTRO'},
              {
                student: <b key='s'>{request.student_name}</b>,
                subject: <b key='j'>{request.subject || '—'}</b>,
              }
            )}
          </p>

          <div className='row g-4 mb-6 mt-2'>
            <div className='col-md-6'>
              <div className='text-muted fs-8 text-uppercase fw-bold mb-1'>
                {intl.formatMessage({id: 'COUNSELLING.EXTERNAL.STUDENT'})}
              </div>
              <div className='fw-semibold text-gray-900'>{request.student_name}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-8 text-uppercase fw-bold mb-1'>
                {intl.formatMessage({id: 'COUNSELLING.EXTERNAL.SUBJECT'})}
              </div>
              <div className='fw-semibold text-gray-900'>{request.subject || '—'}</div>
            </div>
          </div>

          {alreadySubmitted ? (
            <>
              <div className='alert alert-success d-flex align-items-center'>
                <i className='bi bi-check-circle-fill fs-2 me-3 text-success' />
                <div>{intl.formatMessage({id: 'COUNSELLING.EXTERNAL.ALREADY_SUBMITTED'})}</div>
              </div>
              {/* Read-only: submitting is a one-time action, so what they wrote
                  is shown back rather than left editable. */}
              <div className='card border mt-4'>
                <div
                  className='card-body'
                  style={{fontFamily: 'Georgia, serif', lineHeight: 1.6}}
                  dangerouslySetInnerHTML={{__html: request.recommendation ?? ''}}
                />
              </div>
            </>
          ) : (
            <>
              <div className='mb-4'>
                <label className='form-label fw-semibold required'>
                  {intl.formatMessage({id: 'COUNSELLING.EXTERNAL.YOUR_RECOMMENDATION'})}
                </label>
                <TextEditor
                  className='counselling-review-editor'
                  acceptImage={false}
                  value={recommendation}
                  onChange={(value: string) => {
                    setRecommendation(value)
                    if (recommendationError && hasEditorContent(value)) {
                      setRecommendationError(null)
                    }
                  }}
                />
                {recommendationError && (
                  <div className='invalid-feedback d-block mt-2'>{recommendationError}</div>
                )}
              </div>

              <div className='alert alert-warning d-flex align-items-center'>
                <i className='bi bi-info-circle-fill fs-3 me-3 text-warning' />
                <div>{intl.formatMessage({id: 'COUNSELLING.EXTERNAL.ONCE_ONLY'})}</div>
              </div>

              <button className='btn btn-success' disabled={submitting} onClick={submit}>
                {submitting && (
                  <span className='spinner-border spinner-border-sm me-2' role='status' />
                )}
                {intl.formatMessage({id: 'COUNSELLING.EXTERNAL.SUBMIT'})}
              </button>
            </>
          )}
        </KTCardBody>
      </KTCard>
    </div>
  )
}

export default ExternalRecommendation
